import React, { useEffect, useRef, useState } from 'react'
import { Wrapper } from '../style'
import { Table, Modal, Input, Select, message } from 'antd';
import { MakeApiCall } from "../../../../../apis";

function AmazonAdvertising({ loading, list, userData }) {
  const clientId = "amzn1.application-oa2-client.c2d27c6ff2ab4e36a32810c448b9820f";
  const redirectUri = "https://app.sellerview.ai/settings/marketplace-credentials?tab=advertising";
  const scope = "advertising::campaign_management";
  const responseType = "code";

  const hasCalledApi = useRef(false);

  // Modal for seller name + marketplace input
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // Modal for advertising profile selection
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const [sellerName, setSellerName] = useState(localStorage.getItem("ads_sellerName") || "");
  const [marketplaceId, setMarketplaceId] = useState(localStorage.getItem("ads_marketplaceId") || "");
  const [adProfiles, setAdProfiles] = useState([]); // list of profiles returned from API
  const [selectedAdProfileId, setSelectedAdProfileId] = useState(""); // selected profile

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  const openProfileModal = () => setIsProfileModalOpen(true);
  const closeProfileModal = () => setIsProfileModalOpen(false);


  const handleAmazonLogin = () => {
    localStorage.setItem("ads_sellerName", sellerName);
    localStorage.setItem("ads_marketplaceId", marketplaceId);

    const baseUrl = "https://www.amazon.com/ap/oa";
    const params = new URLSearchParams({
      client_id: clientId,
      scope,
      response_type: responseType,
      redirect_uri: redirectUri,
    });

    const finalUrl = `${baseUrl}?${params.toString()}`;
    window.location.href = finalUrl;
  };

  const saveSelectedProfile = async () => {
    if (!selectedAdProfileId) {
      message.error("Please select an advertising profile");
      return;
    }
    try {
      const saveResp = await MakeApiCall(
          `advertising-credentials/save-advertising-profile`,
          "post",
          { ad_profile_ids: [selectedAdProfileId] }, // only selected one
          true,
          {},
          userData?.token
      );
      console.log("Saved Advertising Profile:", saveResp);
      message.success("Advertising profile saved successfully");
      closeProfileModal(); // Close the modal on success

      setTimeout(() => {
        window.location.reload();
      }, 1000); // small delay to let the success message show
    } catch (err) {
      console.error("Error saving advertising profile:", err);
      message.error("Failed to save advertising profile");
    }
  };

  // New Modal component for profile selection
  const ProfileSelectionModal = () => (
      <Modal
          title="Select Advertising Profile"
          open={isProfileModalOpen}
          onCancel={closeProfileModal}
          footer={[
            <button key="back" className="btn btn-light-secondary" onClick={closeProfileModal}>
              Cancel
            </button>,
            <button
                key="submit"
                className="btn btn-primary"
                disabled={!selectedAdProfileId}
                onClick={saveSelectedProfile}
            >
              Save Selected Profile
            </button>,
          ]}
      >
        <div className="mb-3">
          <label className="mb-2">Advertising Profile</label>
          <Select
              style={{ width: "100%" }}
              value={selectedAdProfileId}
              onChange={setSelectedAdProfileId}
              placeholder="Select Ad Profile"
          >
            {adProfiles.map(p => (
                <Select.Option key={p.profileId} value={p.profileId.toString()}>
                  {p.accountInfo?.name} ({p.countryCode}) – {p.profileId}
                </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
  );

  useEffect(() => {
    const handleCallback = async () => {
      const url = new URL(window.location.href);
      const ad_code = url.searchParams.get("code");
      const ad_scope = url.searchParams.get("scope");

      if (!ad_code || !ad_scope) {
        if (window.history.replaceState) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
        return;
      }

      if (!hasCalledApi.current) {
        hasCalledApi.current = true;

        const savedSellerName = localStorage.getItem("ads_sellerName") || "Unknown Seller";
        const savedMarketplaceId = localStorage.getItem("ads_marketplaceId") || "Unknown Marketplace";

        try {
          // Step 1: Call callback-url API
          const response = await MakeApiCall(
              `advertising-credentials/callback-url`,
              "post",
              {
                ad_code,
                scope: ad_scope,
                seller_name: savedSellerName,
                marketplace_id: savedMarketplaceId,
              },
              true,
              {},
              userData?.token
          );

          console.log("Amazon Ads API Raw Response:", response);

          let parsedData = [];
          try {
            parsedData = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
          } catch (err) {
            console.error("Failed to parse response.data:", err);
          }

          if (response?.status && Array.isArray(parsedData) && parsedData.length > 0) {
            setAdProfiles(parsedData); // keep full profile list for dropdown
            if (parsedData.length === 1) {
              setSelectedAdProfileId(parsedData[0].profileId.toString());
            }
            // ************ KEY CHANGE: Open the profile selection modal ************
            setIsProfileModalOpen(true);
          } else if (response?.status) {
            message.warning("No advertising profiles found for the provided credentials.");
          } else {
            message.error("Failed to retrieve advertising profiles.");
          }


          // Clean up URL
          if (window.history.replaceState) {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }

          localStorage.removeItem("ads_sellerName");
          localStorage.removeItem("ads_marketplaceId");

        } catch (err) {
          console.error("Error in Ads API flow:", err);
          message.error("An error occurred during Amazon Ads login process.");
        }
      }
    };

    handleCallback();
  }, [userData?.token]);
  // Removed saveSelectedProfile and ProfileSelectionModal from outside the component,
  // making ProfileSelectionModal an inner component or placed it above useEffect as a function.

  const columns = [
    { title: 'Credential Type', dataIndex: 'credential_type', key: 'credential_type' },
    { title: 'Seller Name', dataIndex: 'seller_name', key: 'seller_name' },
    { title: 'Region', dataIndex: 'region', key: 'region' },
    { title: 'Marketplace', dataIndex: 'marketplace', key: 'marketplace' },
    { title: 'Marketplace ID', dataIndex: 'marketplace_id', key: 'marketplace_id' },
    { title: 'Country Code', dataIndex: 'country_code', key: 'country_code' },
    { title: 'Currency Code', dataIndex: 'currency_code', key: 'currency_code' },
    { title: 'Time Zone', dataIndex: 'time_zone', key: 'time_zone' },
    { title: 'Profile ID', dataIndex: 'profile_id', key: 'profile_id' },
  ];

  const marketplaces = [
    { id: "ATVPDKIKX0DER", name: "Amazon.com (US)" },
    { id: "A1AM78C64UM0Y8", name: "Amazon.com.mx (Mexico)" },
    { id: "A2EUQ1WTGCTBG2", name: "Amazon.ca (Canada)" },
    { id: "A1PA6795UKMFR9", name: "Amazon.de (Germany)" },
    { id: "A1RKKUPIHCS9HS", name: "Amazon.es (Spain)" },
    { id: "A13V1IB3VIYZZH", name: "Amazon.fr (France)" },
    { id: "A21TJRUUN4KGV", name: "Amazon.in (India)" },
    { id: "APJ6JRA9NG5V4", name: "Amazon.it (Italy)" },
    { id: "A1F83G8C2ARO7P", name: "Amazon.co.uk (UK)" },
    { id: "A1805IZSGTT6HS", name: "Amazon.nl (Netherlands)" },
    { id: "A2NODRKZP88ZB9", name: "Amazon.se (Sweden)" },
    { id: "A1C3SOZRARQ6R3", name: "Amazon.pl (Poland)" },
    { id: "A33AVAJ2PDY3EV", name: "Amazon.tr (Turkey)" },
    { id: "A39IBJ37TRP1C6", name: "Amazon.ae (UAE)" },
    { id: "A2VIGQ35RCS4UG", name: "Amazon.sa (Saudi Arabia)" },
    { id: "A19VAU5U5O7RUS", name: "Amazon.sg (Singapore)" },
    { id: "A17E79C6D8DWNP", name: "Amazon.com.au (Australia)" },
    { id: "A1VC38T7YXB528", name: "Amazon.co.jp (Japan)" },
    { id: "AAHKV2X7AFYLW", name: "Amazon.cn (China)" },
    { id: "ARBP9OOSHTCHU", name: "Amazon.com.br (Brazil)" },
  ];

  return (
      <Wrapper>
        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Amazon Advertising Credentials List</h3>
                <div className="card-toolbar">
                  {(!list || list.length === 0) && (
                      <button className="btn btn-light-google" onClick={openLoginModal}>
                        Login With Amazon
                      </button>
                  )}
                </div>
              </div>
              <div className="card-body py-2">
                <div className="table-responsive">
                  <Table
                      scroll={{ x: "max-content" }}
                      columns={columns}
                      loading={loading}
                      dataSource={list}
                      rowKey="key"
                      pagination={false}
                  />
                </div>
                {/* REMOVED: The selection dropdown section is now in ProfileSelectionModal */}
              </div>
            </div>
          </div>
        </div>

        {/* Modal for seller name + marketplace */}
        <Modal
            title="Amazon Ads Login"
            open={isLoginModalOpen}
            onCancel={closeLoginModal}
            onOk={handleAmazonLogin}
            okText="Continue"
            okButtonProps={{ disabled: !sellerName.trim() || !marketplaceId }}
        >
          <div className="mb-3">
            <label>Seller Name</label>
            <Input
                value={sellerName}
                onChange={(e) => setSellerName(e.target.value)}
                placeholder="Enter Seller Name"
            />
          </div>

          <div className="mb-3">
            <label>Marketplace</label>
            <Select
                style={{ width: "100%" }}
                value={marketplaceId}
                onChange={setMarketplaceId}
                placeholder="Select Marketplace"
            >
              {marketplaces.map(m => (
                  <Select.Option key={m.id} value={m.id}>
                    {m.name}
                  </Select.Option>
              ))}
            </Select>
          </div>
        </Modal>

        {/* New Modal for Profile Selection */}
        {adProfiles.length > 0 && <ProfileSelectionModal />}

      </Wrapper>
  );
}

export default AmazonAdvertising;